import produce, { Draft } from 'immer';
import { values } from 'lodash';
import { Query } from 'redux-little-router';

import { Action } from '../actions';
import {
    CampaignIdAndMetrikaCountersInput,
    CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG,
    CHANGE_CAMPAIGN_BANNER_LANGUAGE,
    CHANGE_CAMPAIGN_PRICE_PACKAGE_FOR_CAMPAIGNS_EDIT,
    CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS,
    CHANGE_CONSIDER_COMPETITORS_FLAG,
    CHANGE_HAS_EXTENDED_GEO_TARGETING,
    CHANGE_IS_ALONE_TRAFARET_ALLOWED,
    CHANGE_MARK_TARGET_CALL_LINKS_FLAG,
    CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS,
    CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG,
    CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS,
    ChangeNotificationsDisableValueForEditCampaigns,
    FETCH_CAMPAIGN_AB_SEGMENTS,
    FETCH_CAMPAIGN_AB_SEGMENTS_FOR_NEW_CAMPAIGN,
    FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS,
    FETCH_GOALS_AVAILABLE_FOR_KEY_GOALS_FOR_NEW_CAMPAIGNS,
    GET_CAMPAIGNS_FOR_EDIT,
    RESET_CAMPAIGN_EXPERIMENTS,
    SET_METRIKA_GOALS_DATA_FOR_STRATEGY,
    SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS,
    SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR,
    SHOW_CAMPAIGN_EDIT_ITEM_ERRORS,
    START_SAVE_CAMPAIGNS,
    SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS,
    SynchronizeVisitingCardForEditCampaigns,
    TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY,
    TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY,
    UPDATE_CAMPAIGN_AB_SEGMENTS_METRIKA_UNAVAILABLE,
    UPDATE_CAMPAIGN_AB_SEGMENTS_SUCCESS,
    UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE,
    UPDATE_CAMPAIGN_ARCHIVED_EXPERIMENTS,
    UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS,
    UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE,
    UPDATE_CAMPAIGN_BANNED_VIDEO_DOMAINS_VALUE,
    UPDATE_CAMPAIGN_CHAT,
    UPDATE_CAMPAIGN_DISABLED_IPS_VALUE,
    UPDATE_CAMPAIGN_EXPERIMENTS,
    UPDATE_CAMPAIGN_EXPERIMENTS_RETARGETING_CONDITIONS,
    UPDATE_CAMPAIGN_EXPERIMENTS_VALUE,
    UPDATE_CAMPAIGN_METRIKA_COUNTERS,
    UPDATE_CAMPAIGN_METRIKA_KEY_GOALS,
    UPDATE_CAMPAIGN_METRIKA_MARK_LINKS_FLAG,
    UPDATE_CAMPAIGN_MINUS_PHRASES,
    UPDATE_CAMPAIGN_NAME,
    UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE,
    UPDATE_CAMPAIGN_PLACE,
    UPDATE_CAMPAIGN_RATE_CORRECTIONS,
    UPDATE_CAMPAIGN_RELEVANT_PHRASES,
    UPDATE_CAMPAIGN_SERVICE,
    UPDATE_CAMPAIGN_STRATEGY,
    UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS,
    UPDATE_CAMPAIGNS_ORGANIZATION_IDS,
    UPDATE_DATA_FOR_EDIT_CAMPAIGNS,
    UPDATE_METRO_STATIONS_NAME_FOR_EDIT_CAMPAIGNS,
    UPDATE_PRICE_PACKAGES_FOR_CAMPAIGNS_EDIT,
    UPDATE_RELEVANT_PHRASES_GOALS_FOR_EDIT_CAMPAIGNS,
    UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT,
    UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS,
    UpdateCampaignArchivedExperimentsInput,
    UpdateCampaignAvailableForKeyGoalsPayload,
    UpdateVisitingCardsForEditCampaigns
} from '../actions/campaignsEdit';
import {
    mapCampaignBroadMatchGoalsFromApiFormat,
    mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue
} from '../mappers/campaignsEdit/campaignsEdit';
import { getValueFromApiFormat } from '../mappers/common/common';
import { EntityType, mapEditorKeyToEntityId } from '../mappers/organization/organization';
import { getCampaignEditPageEditorId } from '../service/CampaignsEditHelpers/CampaignsEditHelpers';
import { CampaignsIdsForCampaignsEditHelpers } from '../service/CampaignsIdsForCampaignsEditHelpers';
import { getVisitingCardMetroStationName } from '../service/VisitingCardEditorHelpers/VisitingCardEditorHelpers';
import { CampaignsScreenUrlSearchQueryParams } from '../typings/campaigns';
import { EditCampaignsState, EditCampaignValueField } from '../typings/campaignsEdit';
import { PageStatus } from '../typings/common';
import { CampaignId } from '../typings/domain';
import {
    UpdateCampaignExperimentConditionsItem,
    UpdateCampaignExperimentsValuePayload
} from '../typings/experimentsEditor';
import { State } from '../typings/state';
import { VisitingCardValueEditCampaign } from '../typings/VisitingCardEditor';

import { experimentsEditorInitialState } from './experimentsEditor';

export const EditCampaignInitialState: EditCampaignsState = {
    campaignsIds: [],
    campaigns: {},
    isCreatePage: false,
    pageStatus: PageStatus.LOADING,
    globalError: undefined,
    generalErrors: [],
    retargetingConditions: undefined
};

export const getEditCampaignsPageData = (queryParams: Query): EditCampaignsState => {
    const editCampaignsLocalData = CampaignsIdsForCampaignsEditHelpers.getCampaignsIds(queryParams.ulogin);

    const editCampaignsDataFromUrl = {
        ...EditCampaignInitialState,
        campaignsIds: queryParams[CampaignsScreenUrlSearchQueryParams.campaignsIds]
            ? queryParams[CampaignsScreenUrlSearchQueryParams.campaignsIds].split(',')
            : [],
        isCreatePage: queryParams[CampaignsScreenUrlSearchQueryParams.isNew] === '1',
        campaignType: queryParams[CampaignsScreenUrlSearchQueryParams.campaignType]
    };

    const hasNotEmptyEditCampaignDataFromUrl =
        editCampaignsDataFromUrl.campaignsIds.length !== 0 || editCampaignsDataFromUrl.isCreatePage;

    const pageParamsData = hasNotEmptyEditCampaignDataFromUrl ? editCampaignsDataFromUrl : editCampaignsLocalData;

    return {
        ...EditCampaignInitialState,
        ...pageParamsData
    };
};

export const changeNotificationDisableValueForEditCampaigns = (
    draftState: Draft<EditCampaignsState>,
    action: ChangeNotificationsDisableValueForEditCampaigns,
    state: EditCampaignsState
): void => {
    const { campaignId, disableValue, index } = action;

    const { notifications } = draftState.campaigns[campaignId].value[EditCampaignValueField.notifications];

    notifications[index].disabled = disableValue;
    notifications[index].value = disableValue ? false : notifications[index].value;
};

export const synchronizeVisitingCardForEditCampaigns = (
    action: SynchronizeVisitingCardForEditCampaigns,
    draftState: Draft<EditCampaignsState>,
    state: EditCampaignsState,
    fullState: State
): void => {
    const { campaignId } = action;
    const editorId = getCampaignEditPageEditorId(campaignId);
    const { visitingCardEditor } = fullState;
    const currentVisitingCard = visitingCardEditor[editorId];

    if (currentVisitingCard) {
        draftState.campaigns[campaignId].value[EditCampaignValueField.visitingCard] = {
            ...mapVisitingCardEditCampaignEditorValueToVisitingCardEditCampaignValue(currentVisitingCard),
            vCardId: currentVisitingCard.vCardId
        };
    }
};

export const updateVisitingCardForEditCampaigns = (
    action: UpdateVisitingCardsForEditCampaigns,
    draftState: Draft<EditCampaignsState>,
    state: EditCampaignsState
): void => {
    const { metroStations, vCardsData } = action;
    const { campaigns } = draftState;

    Object.keys(vCardsData).forEach(vCardId => {
        values(campaigns).forEach(campaign => {
            const visitingCard: VisitingCardValueEditCampaign = campaign.value[EditCampaignValueField.visitingCard];

            if (visitingCard.vCardId === vCardId) {
                const { info } = visitingCard;
                const currentMetroStationName = info ? info.metroStationName : null; // в тайпингах визитки использутеся null
                const newInfo = vCardsData[vCardId];

                // ВАЖНО! На бэке хранится тектовое представление станции метро
                // Однако, более точное название (а вдруг), хранится в результатах выполнения ручки на получение всех станций, там данные более актуальные
                // Если удалось вытащить такое имя - при отправке на бэк в мутации отправим уже более точную информацию
                const metroStationName = getVisitingCardMetroStationName(
                    newInfo.geoId,
                    getValueFromApiFormat(newInfo.metroStationId),
                    metroStations
                );

                campaign.value[EditCampaignValueField.visitingCard] = {
                    vCardId,
                    info: {
                        ...newInfo,
                        metroStationName: metroStationName ? metroStationName : currentMetroStationName
                    },
                    isShortVersionChecked: false,
                    isNotShowMetroInSearchChecked: newInfo.metroStationId === '0'
                };
            }
        });
    });
};

export function campaignsEdit(
    state: EditCampaignsState = EditCampaignInitialState,
    action: Action,
    fullState: State
): EditCampaignsState {
    return produce(state, (draftState: Draft<EditCampaignsState>) => {
        switch (action.type) {
            case SHOW_CAMPAIGN_EDIT_GLOBAL_ERROR: {
                draftState.globalError = action.error;
                draftState.pageStatus = PageStatus.ERROR;
                break;
            }

            case SHOW_CAMPAIGN_EDIT_GENERAL_ERRORS: {
                draftState.generalErrors = action.errors;
                break;
            }

            case SHOW_CAMPAIGN_EDIT_ITEM_ERRORS: {
                draftState.campaigns[action.campaignId].errors[action.errorKey] = action.errors;
                break;
            }

            case CHANGE_CAMPAIGNS_EDIT_PAGE_STATUS: {
                draftState.pageStatus = action.status;
                break;
            }

            case GET_CAMPAIGNS_FOR_EDIT: {
                draftState.pageStatus = PageStatus.LOADING;
                break;
            }

            case UPDATE_DATA_FOR_EDIT_CAMPAIGNS: {
                const { constants, campaigns } = action.data;
                draftState.campaigns = campaigns;
                draftState.constants = constants;
                draftState.pageStatus = PageStatus.LOADED;
                break;
            }

            case START_SAVE_CAMPAIGNS: {
                draftState.pageStatus = PageStatus.SAVING;
                break;
            }

            case TOGGLE_CAMPAIGN_OTHER_SETTINGS_VISIBILITY: {
                draftState.campaigns[action.campaignId].areOtherSettingsVisible = !state.campaigns[action.campaignId]
                    .areOtherSettingsVisible;
                break;
            }

            case TOGGLE_CAMPAIGNS_OTHER_SETTINGS_VISIBILITY: {
                action.campaignIds.forEach((campaignId: string) => {
                    const campaign = draftState.campaigns[campaignId];

                    if (campaign) {
                        campaign.areOtherSettingsVisible = true;
                    }
                });
                break;
            }

            case UPDATE_CAMPAIGN_NAME: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.name] = action.name;
                break;
            }

            case UPDATE_CAMPAIGN_DISABLED_IPS_VALUE: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.disabledIps] = action.value;
                break;
            }

            case UPDATE_CAMPAIGN_CHAT: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.chatOperator] = action.chatValue;
                break;
            }

            case UPDATE_CAMPAIGN_RELEVANT_PHRASES: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.relevantPhrases] =
                    action.relevantPhrases;
                break;
            }

            case CHANGE_BANNERS_TEXT_SUBSTITUTION_FLAG: {
                draftState.campaigns[action.campaignId].value[
                    EditCampaignValueField.hasBannersTextSubstitution
                ] = !draftState.campaigns[action.campaignId].value[EditCampaignValueField.hasBannersTextSubstitution];
                break;
            }

            case CHANGE_HAS_EXTENDED_GEO_TARGETING: {
                draftState.campaigns[action.campaignId].value[
                    EditCampaignValueField.hasExtendedGeoTargeting
                ] = !draftState.campaigns[action.campaignId].value[EditCampaignValueField.hasExtendedGeoTargeting];
                break;
            }

            case UPDATE_CAMPAIGN_NOTIFICATIONS_VALUE: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.notifications] =
                    action.notificationsValue;
                break;
            }

            case CHANGE_SITE_MONITORING_FLAG_FOR_EDIT_CAMPAIGNS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.siteMonitoring] = !draftState
                    .campaigns[action.campaignId].value[EditCampaignValueField.siteMonitoring];
                break;
            }

            case CHANGE_SHOW_YANDEX_CATALOG_INFO_FLAG: {
                draftState.campaigns[action.campaignId].value[
                    EditCampaignValueField.showYandexCatalogInfo
                ] = !draftState.campaigns[action.campaignId].value[EditCampaignValueField.showYandexCatalogInfo];
                break;
            }

            case CHANGE_CONSIDER_COMPETITORS_FLAG: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.considerCompetitors] = !draftState
                    .campaigns[action.campaignId].value[EditCampaignValueField.considerCompetitors];
                break;
            }

            case CHANGE_IS_ALONE_TRAFARET_ALLOWED: {
                draftState.campaigns[action.campaignId].value[
                    EditCampaignValueField.isAloneTrafaretAllowed
                ] = !draftState.campaigns[action.campaignId].value[EditCampaignValueField.isAloneTrafaretAllowed];
                break;
            }

            case CHANGE_MARK_TARGET_CALL_LINKS_FLAG: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.markTargetCallLinks] = !draftState
                    .campaigns[action.campaignId].value[EditCampaignValueField.markTargetCallLinks];
                break;
            }

            case CHANGE_CAMPAIGN_BANNER_LANGUAGE: {
                const campaignData = draftState.campaigns[action.campaignId];

                campaignData.value[EditCampaignValueField.bannersLanguage] = action.languageCode;
                campaignData.errors[EditCampaignValueField.bannersLanguage] = [];
                break;
            }

            case UPDATE_CAMPAIGN_BANNED_DOMAINS_VALUE: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.bannedDomains] = action.value;

                break;
            }

            case UPDATE_CAMPAIGN_BANNED_VIDEO_DOMAINS_VALUE: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.bannedVideoDomains] = action.value;

                break;
            }

            case UPDATE_CAMPAIGN_ALLOWED_PAGE_IDS_VALUE: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.allowedPageIds] = action.value;
                break;
            }

            case UPDATE_CAMPAIGN_METRIKA_COUNTERS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.metrikaCounters] =
                    action.metrikaCounters.counters;
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.markMetrikaLinks] =
                    action.metrikaCounters.markMetrikaLinks;
                break;
            }

            case UPDATE_CAMPAIGN_METRIKA_MARK_LINKS_FLAG: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.markMetrikaLinks] =
                    action.isEnabled;
                break;
            }

            case UPDATE_CAMPAIGN_METRIKA_KEY_GOALS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.metrikaKeyGoals].selectedGoals =
                    action.selectedGoals;
                break;
            }

            case UPDATE_CAMPAIGN_EXPERIMENTS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.experiments] = {
                    ...action.experimentsValue
                };
                break;
            }

            case UPDATE_CAMPAIGN_RATE_CORRECTIONS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.rateCorrections] =
                    action.rateCorrections;
                break;
            }

            case UPDATE_RETARGETING_CONDITIONS_FOR_CAMPAIGNS_EDIT: {
                draftState.retargetingConditions = action.retargetingConditions;
                break;
            }

            case UPDATE_CAMPAIGN_SERVICE: {
                const { campaignId, service } = action;

                draftState.campaigns[campaignId].value[EditCampaignValueField.service] = service;

                break;
            }

            case UPDATE_CAMPAIGN_PLACE: {
                const { campaignId, place } = action;

                draftState.campaigns[campaignId].value[EditCampaignValueField.place] = place;

                break;
            }

            case UPDATE_CAMPAIGN_STRATEGY: {
                const { campaignId, strategy } = action;

                draftState.campaigns[campaignId].value[EditCampaignValueField.strategy] = strategy;

                break;
            }

            case UPDATE_CAMPAIGN_MINUS_PHRASES: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.minusPhrases] = {
                    ...draftState.campaigns[action.campaignId].value[EditCampaignValueField.minusPhrases],
                    ...action.value
                };
                break;
            }

            case UPDATE_CAMPAIGN_TIME_TARGETING_FOR_EDIT_CAMPAIGNS: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.timeTargeting] = action.value;
                break;
            }

            case FETCH_CAMPAIGNS_GOALS_AVAILABLE_FOR_KEY_GOALS: {
                action.fetchAvailableForKeyGoalsInput.forEach(({ campaignId }) => {
                    draftState.campaigns[campaignId].availableForKeyGoals = {};
                    draftState.campaigns[campaignId].isAvailableForKeyGoalsLoading = true;
                });

                break;
            }

            case FETCH_GOALS_AVAILABLE_FOR_KEY_GOALS_FOR_NEW_CAMPAIGNS: {
                const { campaignId } = action;

                draftState.campaigns[campaignId].availableForKeyGoals = {};
                draftState.campaigns[campaignId].isAvailableForKeyGoalsLoading = true;
                break;
            }

            case UPDATE_CAMPAIGN_AVAILABLE_FOR_KEY_GOALS: {
                action.campaignGoals.forEach((campaignGoal: UpdateCampaignAvailableForKeyGoalsPayload) => {
                    const campaign = draftState.campaigns[campaignGoal.campaignId];

                    campaign.availableForKeyGoals = campaignGoal.goals;
                    campaign.isAvailableForKeyGoalsLoading = false;
                });
                break;
            }

            case FETCH_CAMPAIGN_AB_SEGMENTS_FOR_NEW_CAMPAIGN: {
                draftState.campaigns[action.campaignId].abSegments.isLoading = true;
                break;
            }

            case FETCH_CAMPAIGN_AB_SEGMENTS: {
                action.fetchABSegmentsInput.forEach((input: CampaignIdAndMetrikaCountersInput) => {
                    draftState.campaigns[input.campaignId].abSegments.isLoading = true;
                });
                break;
            }

            case UPDATE_CAMPAIGN_AB_SEGMENTS_SUCCESS: {
                const { campaignIds } = action;

                campaignIds.forEach((campaignId: string) => {
                    const campaign = draftState.campaigns[campaignId];

                    campaign.abSegments = {
                        isLoading: false,
                        isMetrikaAvailable: true
                    };
                });
                break;
            }

            case UPDATE_CAMPAIGN_AB_SEGMENTS_METRIKA_UNAVAILABLE: {
                const { campaignIds } = action;

                campaignIds.forEach((campaignId: string) => {
                    const campaign = draftState.campaigns[campaignId];

                    campaign.abSegments = {
                        isLoading: false,
                        isMetrikaAvailable: false
                    };
                });
                break;
            }

            case UPDATE_CAMPAIGN_EXPERIMENTS_RETARGETING_CONDITIONS: {
                const { items } = action;
                items.forEach((item: UpdateCampaignExperimentConditionsItem) => {
                    const experimentsValue =
                        draftState.campaigns[item.campaignId].value[EditCampaignValueField.experiments];

                    experimentsValue.abSegmentRetargetingConditionId = item.retargetingConditionId;
                    experimentsValue.abSegmentStatisticRetargetingConditionId = item.statisticRetargetingConditionId;
                });
                break;
            }

            case UPDATE_CAMPAIGN_ARCHIVED_EXPERIMENTS: {
                const { updateItems } = action;
                updateItems.forEach((item: UpdateCampaignArchivedExperimentsInput) => {
                    const experimentsValue =
                        draftState.campaigns[item.campaignId].value[EditCampaignValueField.experiments];

                    item.archivedExperimentsIds.forEach(archivedExperimentId => {
                        const archivedExperiment = experimentsValue.experimentsTree.find(experiment => {
                            return experiment.id === archivedExperimentId;
                        });

                        // Очистить все сегменты заархивированного эксперимента
                        if (archivedExperiment !== undefined) {
                            archivedExperiment.inner = null;
                        }
                    });

                    item.archivedExperimentsIds.forEach(archivedExperimentId => {
                        experimentsValue.archivedExperimentsMap[archivedExperimentId] = true;
                    });
                });
                break;
            }

            case UPDATE_CAMPAIGN_EXPERIMENTS_VALUE: {
                action.experimentsData.forEach((data: UpdateCampaignExperimentsValuePayload) => {
                    const { campaignId, ...experimentsValue } = data;

                    const campaign = draftState.campaigns[campaignId];

                    campaign.value[EditCampaignValueField.experiments] = {
                        ...campaign.value[EditCampaignValueField.experiments],
                        ...experimentsValue
                    };
                });
                break;
            }

            case RESET_CAMPAIGN_EXPERIMENTS: {
                const { isOpen, ...otherPropsExperimentsEditorInitialState } = experimentsEditorInitialState;

                draftState.campaigns[action.campaignId].value[EditCampaignValueField.experiments] = {
                    ...otherPropsExperimentsEditorInitialState,
                    experimentsTree: [],
                    savedSelectedSegmentsIds: []
                };
                break;
            }

            case CHANGE_NOTIFICATIONS_DISABLE_VALUE_FOR_EDIT_CAMPAIGNS: {
                changeNotificationDisableValueForEditCampaigns(draftState, action, state);
                break;
            }

            case UPDATE_VISITING_CARD_FOR_EDIT_CAMPAIGNS: {
                updateVisitingCardForEditCampaigns(action, draftState, state);
                break;
            }

            case SYNCHRONIZE_VISITING_CARD_FOR_EDIT_CAMPAIGNS: {
                synchronizeVisitingCardForEditCampaigns(action, draftState, state, fullState);
                break;
            }

            case SET_METRIKA_GOALS_DATA_FOR_STRATEGY: {
                Object.keys(action.payload).forEach((campaignId: CampaignId): void => {
                    const campaign = draftState.campaigns[campaignId];

                    if (!campaign) {
                        return;
                    }

                    const [metrikaGoals, specialGoals, metrikaAvailable] = action.payload[campaignId];

                    campaign.strategyGoals = {
                        goalsData: metrikaGoals,
                        turboGoalsData: specialGoals,
                        isLoading: false,
                        isMetrikaAvailable: metrikaAvailable
                    };
                });

                break;
            }

            case UPDATE_CAMPAIGNS_ORGANIZATION_IDS: {
                action.params.forEach(editorParams => {
                    const campaignId = mapEditorKeyToEntityId(editorParams.editorKey, EntityType.campaign);
                    const campaign = draftState.campaigns[campaignId];

                    if (!campaign) {
                        return;
                    }

                    campaign.value.organizationId = editorParams.organizationId;
                });

                break;
            }

            case UPDATE_PRICE_PACKAGES_FOR_CAMPAIGNS_EDIT: {
                draftState.pricePackages = action.pricePackages;
                break;
            }

            case CHANGE_CAMPAIGN_PRICE_PACKAGE_FOR_CAMPAIGNS_EDIT: {
                draftState.campaigns[action.campaignId].value[EditCampaignValueField.pricePackageId] =
                    action.pricePackageId;
                break;
            }

            case UPDATE_METRO_STATIONS_NAME_FOR_EDIT_CAMPAIGNS: {
                action.campaignsIds.forEach(campaignId => {
                    const campaign = draftState.campaigns[campaignId];

                    if (!campaign) {
                        return;
                    }

                    const infoVisitingCard = campaign.value[EditCampaignValueField.visitingCard].info;

                    if (!infoVisitingCard) {
                        return;
                    }

                    let currentMetroStationName = infoVisitingCard.metroStationName;

                    // ВАЖНО! На бэке хранится тектовое представление станции метро
                    // Однако, более точное название (а вдруг), хранится в результатах выполнения ручки на получение всех станций, там данные более актуальные
                    // Если удалось вытащить такое имя - при отправке на бэк в мутации отправим уже более точную информацию
                    const metroStationName = getVisitingCardMetroStationName(
                        infoVisitingCard.geoId,
                        getValueFromApiFormat(infoVisitingCard.metroStationId),
                        action.metroStations
                    );

                    if (!currentMetroStationName && metroStationName) {
                        currentMetroStationName = metroStationName;
                    }
                });

                break;
            }

            case UPDATE_RELEVANT_PHRASES_GOALS_FOR_EDIT_CAMPAIGNS: {
                action.campaignsIds.forEach(campaignId => {
                    const campaign = draftState.campaigns[campaignId];

                    if (!campaign) {
                        return;
                    }

                    campaign.relevantPhrasesGoals = {
                        isMetrikaAvailable: action.broadMatchGoals.isMetrikaAvailable,
                        goalsData: mapCampaignBroadMatchGoalsFromApiFormat(
                            action.broadMatchGoals.campaignGoals,
                            campaignId
                        )
                    };
                });

                break;
            }

            default:
                break;
        }

        return draftState;
    });
}
